import { readonly, ref } from 'vue'

/**
 * "Versão do computador" do dashboard no celular (spec v2, T8/decisão 30): troca o
 * `<meta name="viewport">` para 1024 px. O navegador desenha o desktop e encaixa na tela; a pinça
 * nativa funciona. Estado único por aba (módulo): o `useIsMobile` lê para sair do layout mobile.
 *
 * Fica em `src/utils` (global) e não no módulo Dashboard porque o hook global `useIsMobile` precisa
 * ler o estado, e hook global não importa de módulo de página.
 */
export const CLASSIC_VIEWPORT_WIDTH = 1024
export const CLASSIC_VIEWPORT_CONTENT = `width=${CLASSIC_VIEWPORT_WIDTH}`
/** O mesmo do `public/index.html`; vale se o meta não tiver `content`. */
export const DEFAULT_VIEWPORT_CONTENT = 'width=device-width,initial-scale=1.0'
/** Tempo do `maximum-scale=1` provisório que zera o zoom residual no iOS. */
export const ZOOM_RESET_MS = 300

const forced = ref(false)
export const isClassicViewportForced = readonly(forced)

/** Conteúdo original do meta: lido UMA vez (o da página), nunca o provisório do reset. */
let baseContent: string | null = null
let widthBefore = 0
let zoomResetTimer: ReturnType<typeof setTimeout> | null = null

function viewportMeta(): HTMLMetaElement | null {
  if (typeof document === 'undefined') return null
  return document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
}

function clearZoomReset(): void {
  if (zoomResetTimer !== null) clearTimeout(zoomResetTimer)
  zoomResetTimer = null
}

export function applyClassicViewport(): boolean {
  if (forced.value) return true
  const meta = viewportMeta()
  if (!meta) return false
  clearZoomReset()
  baseContent ??= meta.getAttribute('content') || DEFAULT_VIEWPORT_CONTENT
  // clientWidth = viewport de layout; no iOS o innerWidth segue o visual (muda com a pinça).
  widthBefore = document.documentElement.clientWidth || window.innerWidth
  meta.setAttribute('content', CLASSIC_VIEWPORT_CONTENT)
  forced.value = true
  return true
}

/**
 * Volta ao viewport original. Grava `<original>,maximum-scale=1` e, após `ZOOM_RESET_MS`, o
 * original puro: truque do Safari para zerar o zoom residual sem travar a pinça depois.
 */
export function restoreViewport(): void {
  if (!forced.value) return
  forced.value = false
  const meta = viewportMeta()
  if (!meta) return
  const original = baseContent ?? DEFAULT_VIEWPORT_CONTENT
  meta.setAttribute('content', `${original},maximum-scale=1`)
  clearZoomReset()
  zoomResetTimer = setTimeout(() => {
    zoomResetTimer = null
    if (!forced.value) meta.setAttribute('content', original)
  }, ZOOM_RESET_MS)
}

/** Fator de escala da UI na Clássica: 1024 / largura antes de aplicar; 1 se desconhecida. */
export function classicUiScale(): number {
  if (!widthBefore) return 1
  return Math.max(1, CLASSIC_VIEWPORT_WIDTH / widthBefore)
}

/** Guard de rota: fora do `keepPath` (o `/dashboard`), o app volta ao viewport original. */
export function restoreViewportOutside(path: string, keepPath: string): void {
  if (path === keepPath || path.startsWith(`${keepPath}/`)) return
  restoreViewport()
}

export function resetClassicViewportForTests(): void {
  clearZoomReset()
  forced.value = false
  baseContent = null
  widthBefore = 0
}
